import { GeometryModel, Model, Vector3 } from '@technology/web3d';

/**
 * Well-known source ids used when publishing line-selection paths to a
 * {@link LineSelectionSink}. Centralised here so all capabilities pull from
 * the same set and a typo or duplicate cannot silently collide.
 */
export const LineSelectionSourceId = {
  dataPrep: 'data-prep',
  designPackage: 'design-package',
  /**
   * Catch-all source for line selections on any loaded TrimBIM model
   * (alignments, models panel imports, design packages, etc.). Publishing
   * is driven by a single capability that inspects every selected `.trb`
   * model's geometry rather than a domain-specific owner.
   */
  trimbim: 'trimbim',
} as const;
export type LineSelectionSourceIdValue =
  (typeof LineSelectionSourceId)[keyof typeof LineSelectionSourceId];

/**
 * Reserved substring used by sinks to namespace ids as
 * `<sourceId><SEPARATOR><pathId>`. Source ids must not contain this substring.
 */
export const LINE_SELECTION_ID_SEPARATOR = '::';

/**
 * Minimal contract a section/profile coordinator must satisfy to receive
 * line-selection events from capabilities (e.g. data-prep, design-package).
 *
 * Defined here so capabilities can publish line geometry to a sink without
 * having to import the concrete coordinator package — the concrete
 * implementation lives in `@trimble/path-section-capability` (a feature
 * package), which keeps `@trimble/web3d-capabilities` (util tier) free of
 * feature-tier dependencies.
 */
export interface LineSelectionSink {
  /**
   * Registers a refresher that the sink invokes when it wants the source to
   * re-publish its current selection (e.g. when section generation toggles
   * back on while something is already selected). Returns an unsubscribe
   * function — call it on capability cleanup or when re-attaching.
   */
  registerSource(sourceId: string, refresher: () => void): () => void;

  /**
   * Publishes (or replaces) a path owned by `sourceId`. Implementations may
   * silently ignore the call when disabled or when the path is too short.
   */
  setPath(sourceId: string, pathId: string, path: Vector3[]): void;

  /** Disposes every path currently published by `sourceId`. */
  clearSource(sourceId: string): void;
}

/**
 * Optional protocol that a {@link Model} can implement to notify consumers
 * when its rendered tile/geometry set changes (e.g. LOD shift, streaming
 * tiles in/out). Section pipelines use this to re-extract geometry without
 * polling.
 *
 * Consumers should debounce their reaction — during continuous panning the
 * callback can fire many times per second.
 */
export interface TilesChangedSource {
  /**
   * Subscribe to be notified when this model's rendered tile set changes.
   * @returns an unsubscribe function — call on cleanup or re-subscription.
   */
  onTilesChanged(callback: () => void): () => void;
}

/** Type guard for {@link TilesChangedSource}. */
export function isTilesChangedSource(
  candidate: object
): candidate is TilesChangedSource {
  return (
    typeof (candidate as Partial<TilesChangedSource>).onTilesChanged ===
    'function'
  );
}

/** Type guard for web3d models that expose entity geometry via `getGeometry`. */
export function isGeometryModel(
  candidate: object
): candidate is Model & GeometryModel {
  return 'getGeometry' in candidate;
}
